import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, yen } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { DatePicker } from "../components/DatePicker";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Crown, Medal, Trash2, Phone } from "lucide-react";

const tierStyle = {
  gold: "bg-amber-50 text-amber-700 border-amber-200",
  silver: "bg-slate-100 text-slate-600 border-slate-200",
  baru: "bg-gray-50 text-gray-500 border-gray-200",
};

export default function Pelanggan() {
  const { t } = useI18n();
  const [rows, setRows] = useState([]);
  const [settings, setSettings] = useState(null);

  const load = useCallback(
    () =>
      Promise.all([api.get("/customers"), api.get("/settings")]).then(([c, s]) => {
        setRows(c.data);
        setSettings(s.data);
      }),
    []
  );
  useEffect(() => {
    load();
  }, [load]);

  const pct = (tier) =>
    tier === "gold" ? settings?.gold_pct || 0 : tier === "silver" ? settings?.silver_pct || 0 : 0;

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <div>
        <h1 className="font-display text-3xl font-semibold">{t("loyalty_title")}</h1>
        <p className="text-sm text-muted-foreground">{t("loyalty_sub")}</p>
      </div>

      {settings && (
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="bg-white border border-gray-200 rounded-2xl p-5 flex items-center gap-3">
            <Medal className="text-slate-500" size={20} />
            <div>
              <p className="font-display font-semibold">Silver</p>
              <p className="text-sm text-muted-foreground">
                ≥ {yen(settings.silver_spend)} · {t("discount")} {settings.silver_pct}%
              </p>
            </div>
          </div>
          <div className="bg-white border border-gray-200 rounded-2xl p-5 flex items-center gap-3">
            <Crown className="text-amber-500" size={20} />
            <div>
              <p className="font-display font-semibold">Gold</p>
              <p className="text-sm text-muted-foreground">
                ≥ {yen(settings.gold_spend)} · {t("discount")} {settings.gold_pct}%
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("name")}</TableHead>
                <TableHead>{t("cust_phone")}</TableHead>
                <TableHead className="text-right">{t("qty_sold")}</TableHead>
                <TableHead className="text-right">{t("total")}</TableHead>
                <TableHead>{t("tier")}</TableHead>
                <TableHead>{t("birthday")}</TableHead>
                <TableHead>{t("email")}</TableHead>
                <TableHead className="text-right">{t("action")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                    {t("no_data")}
                  </TableCell>
                </TableRow>
              )}
              {rows.map((c) => (
                <TableRow key={c.id} data-testid={`customer-row-${c.id}`}>
                  <TableCell className="font-medium">{c.name}</TableCell>
                  <TableCell className="text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Phone size={12} /> {c.phone}
                    </span>
                  </TableCell>
                  <TableCell className="text-right num">{c.orders_count}</TableCell>
                  <TableCell className="text-right num">{yen(c.total_spent)}</TableCell>
                  <TableCell>
                    <span
                      className={`text-xs px-2.5 py-1 rounded-full border capitalize ${
                        tierStyle[c.tier]
                      }`}
                    >
                      {c.tier} {pct(c.tier) > 0 ? `· ${pct(c.tier)}%` : ""}
                    </span>
                  </TableCell>
                  <TableCell>
                    <DatePicker
                      testid={`birthday-${c.id}`}
                      className="h-9 w-40"
                      value={c.birthday || ""}
                      placeholder={t("birthday")}
                      toYear={new Date().getFullYear()}
                      onChange={async (v) => {
                        if (v === (c.birthday || "")) return;
                        await api.put(`/customers/${c.id}`, { birthday: v });
                        load();
                        toast.success(t("saved"));
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      data-testid={`email-${c.id}`}
                      placeholder={t("email")}
                      defaultValue={c.email || ""}
                      onBlur={async (e) => {
                        if (e.target.value === (c.email || "")) return;
                        await api.put(`/customers/${c.id}`, { email: e.target.value });
                        load();
                        toast.success(t("saved"));
                      }}
                      className="h-9 w-48"
                    />
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      size="icon"
                      variant="ghost"
                      className="text-destructive"
                      data-testid={`delete-customer-${c.id}`}
                      onClick={async () => {
                        await api.delete(`/customers/${c.id}`);
                        load();
                        toast.success(t("saved"));
                      }}
                    >
                      <Trash2 size={15} />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
